import { useEffect, useRef } from 'react';
import type { Editor } from '@tiptap/core';

import type { TDocument, TDraft, TVersion } from '../types';
import insertSpaceIntoEmptyTags from '../helpers/insertSpaceIntoEmptyTags.helper';
import { loadDocument } from '../largeDocument';

type TUseLoadDocumentContentOptions = {
  editor: Editor | null;
  doc: TDocument;
  version?: TDraft | TVersion;
  data?: string;
  isFetching: boolean;
  blobName?: string;
  isEdit: boolean;
};

/** Loads fetched HTML without turning an unchanged background refetch into an editor rebuild. */
const useLoadDocumentContent = ({
  editor,
  doc,
  version,
  data,
  isFetching,
  blobName,
  isEdit,
}: TUseLoadDocumentContentOptions) => {
  /** The query value most recently put in this editor. */
  const refLoadedContent = useRef<{ editor: Editor; blobName?: string; data: string }>();
  const refWasEdit = useRef(isEdit);
  const refReloadAfterEdit = useRef(false);

  useEffect(() => {
    // A refetch completion only needs to force an otherwise-identical load when it belongs to this
    // transition. Ordinary focus/reconnect refetches must leave an already displayed document alone.
    if (refWasEdit.current && !isEdit) refReloadAfterEdit.current = true;
    refWasEdit.current = isEdit;

    if (!editor || typeof data !== 'string') return;
    const loaded = refLoadedContent.current;
    const isLoadedSource = loaded?.editor === editor && loaded.blobName === blobName;
    /**
     * A cached blob that is being refetched is not shown.
     *
     * Leaving edit mode changes the query key, and react-query answers with the version blob it
     * cached BEFORE the edits while it fetches the current one. Keep the live edited document on
     * screen until that fetch finishes. The pending transition then makes the fresh result load even
     * when React Query preserves the old string because the response is byte-identical.
     *
     * A different document still loads its cached value immediately so its editor does not remain on
     * the placeholder. The edit-to-view flag is the exception because the live editor is the safer
     * content to show during that specific transition.
     */
    if (isFetching && (isLoadedSource || refReloadAfterEdit.current)) return;

    const isAlreadyLoaded = isLoadedSource && loaded.data === data;
    if (isAlreadyLoaded && !refReloadAfterEdit.current) return;

    const html = insertSpaceIntoEmptyTags(data);
    // Large documents go into the editor one part at a time; everything else is unchanged.
    refLoadedContent.current = { editor, blobName, data };
    refReloadAfterEdit.current = false;
    loadDocument({ editor, doc, version, html });
  }, [editor, data, isFetching, blobName, isEdit]);
};

export default useLoadDocumentContent;
